import { useCallback, useMemo, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { ListJobPositionItem } from '@restworld/data-services';
import { EmployerJobListingCard, getJPOnboardingFormLink } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';
import { showContactRestWorldManagerModalAtom } from '../../restaurants/employer-restaurants-filters/employer-restaurants.atoms';
import { useDuplicateJobPosition } from '../hooks/use-duplicate-job-position';
import { useDeleteJobPositionDraft } from '../hooks/use-delete-job-position-draft';
import { usePutOfflineJobPosition } from '../hooks/use-put-offline-job-position';
import DeleteJobPositionDraftModal from '../modals/delete-job-position-draft-modal';
import PutOfflineJobPositionConfirmationModal from '../modals/put-offline-job-position-confirmation-modal';
import PutOfflineJobPositionNPSModal from '../modals/put-offline-job-position-nps-modal';
import PutOfflineJobPositionSuccessModal from '../modals/put-offline-job-position-success-modal';
import { PPUEditInfoModal } from '../modals/ppu-edit-info-modal';
import { getEmployerJPState } from '@restworld/utils-common';

interface EmployerJobListingCardsProps {
  jobPositionItems?: ListJobPositionItem[];
}

function EmployerJobListingCardsList({ jobPositionItems }: EmployerJobListingCardsProps) {
  const router = useRouter();
  const { user } = useAuth();
  const isUsingPPU = user?.using_ppu_business_model === true;

  // PPU edit info modal state
  const [ppuEditJpId, setPPUEditJpId] = useState<string | null>(null);

  const { duplicateJobPosition } = useDuplicateJobPosition();
  const { isDeleting, isDeleteModalOpen, handleDeleteDraft, handleConfirmDelete, handleCloseDeleteModal } =
    useDeleteJobPositionDraft();
  const {
    isPuttingOffline,
    isSubmittingFeedback,
    isConfirmationModalOpen,
    isNPSModalOpen,
    isSuccessModalOpen,
    ppuPaidUntil,
    isSubscriptionFeedbacksSubmitted,
    handleOpenPutOffline,
    handleConfirmPutOffline,
    handleSubmitNPSFeedback,
    handleCloseConfirmationModal,
    handleCloseNPSModal,
    handleCloseSuccessModal,
  } = usePutOfflineJobPosition();

  const handlePutOnline = useCallback(
    (jpId: string) => {
      router.push(`/employer/job-positions/onboarding?job_position_id=${jpId}&step=review`);
    },
    [router]
  );

  const jobListingCards = useMemo(
    () =>
      !!jobPositionItems?.length && (
        <>
          {jobPositionItems.map((jp) => (
            <JobPositionItem
              key={jp.job_position.id}
              jobPositionItem={jp}
              isUsingPPU={isUsingPPU}
              onPPUEditInfo={() => setPPUEditJpId(jp.job_position.id)}
              onDeleteDraft={handleDeleteDraft}
              onPutOffline={handleOpenPutOffline}
              onPutOnline={handlePutOnline}
            />
          ))}
        </>
      ),
    [jobPositionItems, isUsingPPU, handleDeleteDraft, handleOpenPutOffline, handlePutOnline]
  );

  return (
    <>
      {jobListingCards}
      <DeleteJobPositionDraftModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
      <PutOfflineJobPositionConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmPutOffline}
        isLoading={isPuttingOffline}
        isSubscriptionFeedbacksSubmitted={isSubscriptionFeedbacksSubmitted}
        ppuPaidUntil={ppuPaidUntil}
      />
      <PutOfflineJobPositionNPSModal
        isOpen={isNPSModalOpen}
        onClose={handleCloseNPSModal}
        onSubmit={handleSubmitNPSFeedback}
        isLoading={isSubmittingFeedback || isPuttingOffline}
      />
      <PutOfflineJobPositionSuccessModal isOpen={isSuccessModalOpen} onClose={handleCloseSuccessModal} />
      <PPUEditInfoModal
        isOpen={!!ppuEditJpId}
        onClose={() => setPPUEditJpId(null)}
        onDuplicate={() => {
          if (ppuEditJpId) {
            duplicateJobPosition({ jobPositionId: ppuEditJpId });
          }
          setPPUEditJpId(null);
        }}
      />
    </>
  );
}

export default EmployerJobListingCardsList;

interface JobPositionItemProps {
  jobPositionItem: ListJobPositionItem;
  isUsingPPU?: boolean;
  onPPUEditInfo?: () => void;
  onDeleteDraft: (jpId: string) => void;
  onPutOffline: (jpId: string) => void;
  onPutOnline: (jpId: string) => void;
}

function JobPositionItem({
  jobPositionItem,
  isUsingPPU,
  onPPUEditInfo,
  onDeleteDraft,
  onPutOffline,
  onPutOnline,
}: JobPositionItemProps) {
  const {
    id,
    jobTitle,
    occupation,
    staffRequested,
    status,
    labels,
    currentRestaurant,
    candidates,
    lastStatusChangeDate,
  } = useJobPositionItem(jobPositionItem);

  const setShowContactRestworldManagerModal = useSetAtom(showContactRestWorldManagerModalAtom);
  const { duplicateJobPosition } = useDuplicateJobPosition();

  const handleDuplicate = useCallback(
    (jpId: string) => {
      duplicateJobPosition({ jobPositionId: jpId });
    },
    [duplicateJobPosition]
  );

  const hrefValue = useMemo(() => {
    let href = `/employer/job-positions/${id}`;
    if (['jp_draft', 'jp_approval'].includes(status?.key || '')) {
      const jpState = getEmployerJPState(status?.key);
      href = getJPOnboardingFormLink({
        jpId: id,
        titleDescriptionSet: !!jobTitle,
        jpState,
      });
    }
    return href;
  }, [id, status?.key, jobTitle]);
  return (
    <EmployerJobListingCard
      key={id}
      href={hrefValue}
      jpId={id}
      jobTitle={jobTitle}
      restaurant={currentRestaurant}
      lastStatusChangeDate={lastStatusChangeDate}
      labels={labels}
      occupationLabel={occupation?.label_it.label}
      staffRequested={staffRequested}
      status={status}
      candidates={candidates}
      domainInterestKey={occupation?.domain_interest_var?.key}
      isUsingPPU={isUsingPPU}
      onRequestChanges={() => setShowContactRestworldManagerModal(true)}
      onPPUEditInfo={onPPUEditInfo}
      onDuplicate={handleDuplicate}
      onDeleteDraft={onDeleteDraft}
      onPutOffline={onPutOffline}
      onPutOnline={onPutOnline}
    />
  );
}
function useJobPositionItem({
  job_position,
  number_of_shortlisted_workers,
  all_time_number_of_interested_workers,
  number_of_visits,
  number_of_workers_not_viewed_by_employer,
  applicants_avatar,
}: ListJobPositionItem) {
  const { t: translateCommon } = useTranslation('common');

  const { id, job_title, restaurant, occupation, staff_requested, status, last_status_change_date } =
    job_position || {};

  const labels = useMemo(
    () => ({
      visits: number_of_visits,
      candidates: all_time_number_of_interested_workers,
      qualified: number_of_shortlisted_workers,
    }),
    [all_time_number_of_interested_workers, number_of_shortlisted_workers, number_of_visits]
  );

  const currentRestaurant = useMemo(
    () => ({
      address: restaurant?.address,
      logo: restaurant?.restaurant_logo,
      name: restaurant?.restaurant_name,
    }),
    [restaurant?.address, restaurant?.restaurant_logo, restaurant?.restaurant_name]
  );

  const applicants = useMemo(() => applicants_avatar || [], [applicants_avatar]);

  const employerCandidates = useMemo(
    () =>
      applicants.map((candidate) => ({
        firstName: candidate.name,
        lastName: candidate.surname,
        image: candidate.avatar_url,
      })),
    [applicants]
  );

  let candidatesDisplayMessage: string;
  if (number_of_workers_not_viewed_by_employer === 1)
    candidatesDisplayMessage = translateCommon('new_candidate_to_view');
  else candidatesDisplayMessage = translateCommon('candidates_to_view');

  const candidates = useMemo(
    () => ({
      count: number_of_workers_not_viewed_by_employer,
      candidates: employerCandidates,
      message: candidatesDisplayMessage,
    }),
    [candidatesDisplayMessage, employerCandidates, number_of_workers_not_viewed_by_employer]
  );

  return {
    id,
    jobTitle: job_title,
    occupation,
    staffRequested: staff_requested,
    status,
    labels,
    currentRestaurant,
    candidates,
    lastStatusChangeDate: last_status_change_date,
  };
}
